import {
  Box,
  Button,
  Grid,
  makeStyles,
  Typography,
  TextField
} from '@material-ui/core';
import PhoneTextField from '../../../Core/PhoneTextField/PhoneTextField';
import { isEmpty } from 'lodash';
import {
  useValidators,
  ValidationErrors,
  Validators,
  FieldValidators
} from '../../../Core/Validators';
import { useObserver } from 'mobx-react-lite';
import React, { FC } from 'react';

import { isUndefined } from 'util';

import SubmitErrorMessage from './SubmitErrorMessage';
import { useOrdering } from '../../../../stores/OrderingStore';
import { ViewState } from '../../../../domain/models/ViewState';
import OrderSummary from '../../OrderDetail/OrderSummary';
import { OnlineOrderDeliverySettings } from 'kiolyn-api';
import PromotionCode from './PromotionCode';

const useStyles = makeStyles(theme => ({
  container: {
    flexWrap: 'nowrap'
  },
  message: {
    marginBottom: theme.spacing(2),
    textAlign: 'center'
  },
  submit: {
    margin: theme.spacing(2)
  },
  infoGroup: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    minWidth: 320
  },
  infoGroupContent: {
    padding: theme.spacing(1)
  },
  infoGroupTitle: {
    color: theme.palette.primary.main
  }
}));

interface SubmitProps {
  confirmMessage?: string;
  onErrorsChange: (e: ValidationErrors) => void;
}

const Submit: FC<SubmitProps> = ({ confirmMessage, onErrorsChange }) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const order = orderingStore.order;
  const settings = (orderingStore.store as any)
    .delivery_settings as OnlineOrderDeliverySettings;

  const onChangeToPickupOrder = () => {
    orderingStore.setOrderType('pickup');
    orderingStore.submitCurrentOrder(false);
  };

  const handleValueChange = (name: string, value: any) => {
    const o = order as { [name: string]: any };
    o[name] = value;
    validate(name, value);
  };

  const fieldValidators: FieldValidators = {
    customer_email: [Validators.required, Validators.emailFormat],
    customer_phone: [Validators.required]
  };

  const errorMessages: { [name: string]: { [name: string]: string } } = {
    customer_email: {
      required: 'Email is required',
      emailFormat: 'Email is invalid'
    },
    customer_phone: {
      required: 'Phone is required'
    }
  };

  const [errors, validate] = useValidators(
    order,
    fieldValidators,
    onErrorsChange
  );

  return useObserver(() => (
    <Grid
      container
      className={classes.container}
      direction="column"
      alignItems="center"
    >
      {confirmMessage && confirmMessage.length > 0 && (
        <Typography className={classes.message}>{confirmMessage}</Typography>
      )}
      <Typography color="primary">Your order is ready for SUBMIT</Typography>
      <PromotionCode />
      <Box className={classes.infoGroup}>
        <Typography variant="subtitle1" className={classes.infoGroupTitle}>
          CONTACT INFORMATION
        </Typography>
        <Grid container direction="column" className={classes.infoGroupContent}>
          <TextField
            fullWidth
            margin="dense"
            id="email"
            label="Email"
            type="email"
            name="customer_email"
            key="customer_email"
            value={order.customer_email}
            onChange={e => handleValueChange(e.target.name, e.target.value)}
            required
            error={!isUndefined(errors.customer_email)}
            helperText={errorMessages.customer_email[errors.customer_email]}
            inputProps={{
              maxLength: 100
            }}
          />
          <PhoneTextField
            fullWidth
            margin="dense"
            id="customer_phone"
            name="customer_phone"
            key="customer_phone"
            value={order.customer_phone}
            onChange={e =>
              handleValueChange(
                e.target.name,
                e.target.value.trim().replace(/[^0-9]/g, '')
              )
            }
            required
            error={!isUndefined(errors.customer_phone)}
            errorMessage={errorMessages.customer_phone[errors.customer_phone]}
          />
        </Grid>
      </Box>

      <Box className={classes.infoGroup}>
        <Typography variant="subtitle1" className={classes.infoGroupTitle}>
          ORDER SUMMARY
        </Typography>
        <Box className={classes.infoGroupContent}>
          <OrderSummary order={order} />
        </Box>
      </Box>

      {orderingStore.submitState === ViewState.ERROR && (
        <SubmitErrorMessage
          submitError={orderingStore.submitError}
          message={orderingStore.submitErrorMessage}
        />
      )}
      {settings?.delivery_type === 'postmates' &&
        order.order_type === 'delivery' &&
        orderingStore.submitState === ViewState.ERROR && (
          <>
            <Typography color="primary">- OR -</Typography>
            <Button
              className={classes.submit}
              variant="contained"
              size="large"
              color="primary"
              aria-label="Change to Pick-up Order"
              onClick={onChangeToPickupOrder}
              disabled={
                orderingStore.submitToken.length === 0 || !isEmpty(errors)
              }
            >
              Change to Pick-up and Submit
            </Button>
          </>
        )}
    </Grid>
  ));
};

export default Submit;
